import {
  collection,
  addDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { firestore } from '../config/firebase';

const COLLECTION = 'fundCapitalAdditionProposals';

/**
 * Create a capital addition proposal (investor proposes to add funds).
 * @param {{ fundInvestorId: string, lenderUid: string, investorUserId: string, proposedAmount: number, availableFromDate: string | null, investingEntityType?: 'individual' | 'company', investingCompanyId?: string | null, investingDisplayName?: string | null }} data
 * @returns {Promise<string>} proposal doc id
 */
export async function createCapitalAdditionProposal(data) {
  const {
    fundInvestorId,
    lenderUid,
    investorUserId,
    proposedAmount,
    availableFromDate,
    investingEntityType,
    investingCompanyId,
    investingDisplayName,
  } = data;
  if (!fundInvestorId || !lenderUid || !investorUserId || proposedAmount == null || proposedAmount < 0) {
    throw new Error('fundInvestorId, lenderUid, investorUserId and proposedAmount required');
  }
  const entityType = investingEntityType === 'company' ? 'company' : 'individual';
  const docRef = await addDoc(collection(firestore, COLLECTION), {
    fundInvestorId,
    lenderUid,
    investorUserId,
    proposedAmount: Number(proposedAmount),
    availableFromDate: availableFromDate && String(availableFromDate).trim() ? String(availableFromDate).trim().slice(0, 10) : null,
    investingEntityType: entityType,
    investingCompanyId: investingCompanyId && String(investingCompanyId).trim() ? String(investingCompanyId).trim() : null,
    investingDisplayName: investingDisplayName && String(investingDisplayName).trim() ? String(investingDisplayName).trim() : null,
    status: 'pending',
    createdAt: serverTimestamp(),
    respondedAt: null,
    respondedByUid: null,
  });
  return docRef.id;
}

/**
 * Subscribe to capital addition proposals for a lender.
 * @param {string} lenderUid
 * @param {(items: Array<object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeCapitalAdditionProposalsByLender(lenderUid, callback) {
  if (!lenderUid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('lenderUid', '==', lenderUid),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
          respondedAt: data.respondedAt?.toDate?.() ?? data.respondedAt,
        };
      });
      callback(items);
    },
    (err) => {
      console.error('[fundCapitalAdditionProposals] snapshot error (by lender):', err?.code, err?.message);
      callback([]);
    }
  );
}

/**
 * Subscribe to capital addition proposals for an investor.
 * @param {string} investorUserId
 * @param {(items: Array<object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeCapitalAdditionProposalsByInvestor(investorUserId, callback) {
  if (!investorUserId) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('investorUserId', '==', investorUserId),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
        respondedAt: data.respondedAt?.toDate?.() ?? data.respondedAt,
      };
    });
    callback(items);
  });
}

/**
 * Accept or reject a proposal (lender only). Does NOT update fundInvestor.investedAmount; that happens on confirm receipt.
 * @param {string} proposalId
 * @param {{ status: 'accepted' | 'rejected', respondedByUid: string }} data
 */
export async function respondToCapitalAdditionProposal(proposalId, data) {
  const ref = doc(firestore, COLLECTION, proposalId);
  await updateDoc(ref, {
    status: data.status,
    respondedAt: serverTimestamp(),
    respondedByUid: data.respondedByUid || null,
  });
}

/**
 * Confirm receipt of funds for an accepted proposal (lender only). Sets fundsReceivedAt and fundsReceivedAmount.
 * Caller must also update fundInvestor.investedAmount.
 * @param {string} proposalId
 * @param {{ fundsReceivedAt: string (YYYY-MM-DD), fundsReceivedAmount: number }} data
 */
export async function confirmCapitalAdditionReceipt(proposalId, data) {
  const ref = doc(firestore, COLLECTION, proposalId);
  const dateStr = data.fundsReceivedAt && String(data.fundsReceivedAt).trim().slice(0, 10);
  const amount = data.fundsReceivedAmount != null && !Number.isNaN(Number(data.fundsReceivedAmount)) ? Number(data.fundsReceivedAmount) : null;
  await updateDoc(ref, {
    fundsReceivedAt: dateStr || null,
    fundsReceivedAmount: amount,
    fundsReceivedConfirmedAt: serverTimestamp(),
  });
}
